import React, { useState, useRef, useEffect } from "react";
import { sendMessageToBot, getWelcomeMessage, INITIAL_SUGGESTIONS } from "./chatBotService";
import { formatChatMarkdown } from "./chatMarkdown";
import botAvatarImg from "./bot_avatar.png";
import "./ChatBot.scss";

/**
 * =====================================================================
 * AAKASHGANGA STATIC CHATBOT COMPONENT
 * =====================================================================
 * 
 * Future API Integration Note:
 * - The chatbot is fully modularized.
 * - When your API is ready, update the API call in `chatBotService.js`.
 * - The UI automatically handles the loading state, typing indicator,
 *   and message history without requiring structural changes.
 */

// Helper to format 12-hour timestamp
const formatTime = (date = new Date()) => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// SVG Icons
const Icons = {
  Chat: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Close: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Minimize: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Expand: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  ),
  Refresh: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Send: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  BotAvatar: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <line x1="8" y1="16" x2="8" y2="16.01" />
      <line x1="16" y1="16" x2="16" y2="16.01" />
    </svg>
  ),
  UserAvatar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showGreeting, setShowGreeting] = useState(true);
  const [hasUnread, setHasUnread] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([]);

  // Count messages sent by the user; hide suggestions after 2 user messages
  const userMessageCount = messages.filter((msg) => msg.sender === "user").length;
  const showSuggestions = !isTyping && messages.length > 0 && userMessageCount < 2;

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto scroll to latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
    }
  }, [messages, isTyping, isOpen, isMinimized]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [isOpen, isMinimized]);

  // Fetch dynamic welcome message from the API on mount
  useEffect(() => {
    let isMounted = true;
    getWelcomeMessage().then((greeting) => {
      if (isMounted && greeting) {
        setMessages([
          {
            id: `welcome-${Date.now()}`,
            sender: "bot",
            text: greeting,
            time: formatTime()
          }
        ]);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch dynamic welcome message if user opens and messages is still empty
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      let isMounted = true;
      getWelcomeMessage().then((greeting) => {
        if (isMounted && greeting) {
          setMessages([
            {
              id: `welcome-${Date.now()}`,
              sender: "bot",
              text: greeting,
              time: formatTime()
            }
          ]);
        }
      });
      return () => {
        isMounted = false;
      };
    }
  }, [isOpen, messages.length]);

  // Toggle open / close
  const toggleOpen = () => {
    const nextState = !isOpen;
    setIsOpen(nextState);
    if (nextState) {
      setIsMinimized(false);
      setShowGreeting(false);
      setHasUnread(false);
    }
  };

  // Toggle minimize
  const toggleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized((prev) => !prev);
  };

  // Reset chat history using the welcome API
  const handleClearHistory = async (e) => {
    e.stopPropagation();
    setMessages([]);
    setIsTyping(true);
    try {
      const greeting = await getWelcomeMessage();
      if (greeting) {
        setMessages([
          {
            id: `welcome-${Date.now()}`,
            sender: "bot",
            text: greeting,
            time: formatTime()
          }
        ]);
      }
    } catch (err) {
      console.error("Error fetching welcome message:", err);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle send message
  const handleSendMessage = async (textToSend) => {
    const query = (textToSend || inputValue).trim();
    if (!query || isTyping) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: query,
      time: formatTime()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    try {
      // Map previous messages to the history format expected by the API
      const chatHistory = messages
        .filter((msg) => !msg.id.startsWith("welcome-")) // Exclude welcome messages
        .map((msg) => ({
          role: msg.sender === "bot" ? "assistant" : "user",
          content: msg.text,
        }));

      const botReplyText = await sendMessageToBot(query, chatHistory);
      const botMessage = {
        id: `bot-${Date.now()}`,
        sender: "bot",
        text: botReplyText,
        time: formatTime()
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error receiving bot response:", error);
      const errorMessage = {
        id: `bot-err-${Date.now()}`,
        sender: "bot",
        text: "Sorry, I encountered an issue processing your request. Please try again or email contactus@vikramshilaedu.in.",
        time: formatTime()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle enter key submit
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="ag-chatbot-container">
      {/* Greeting Bubble (shown before user first opens the chat) */}
      {!isOpen && showGreeting && (
        <div className="ag-chatbot-greeting-bubble" onClick={toggleOpen}>
          <span>👋 Have questions? Ask Tara!</span>
          <button
            className="close-greeting"
            onClick={(e) => {
              e.stopPropagation();
              setShowGreeting(false);
            }}
            title="Dismiss greeting"
          >
            ×
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`ag-chatbot-window ${isMinimized ? "minimized" : ""}`}>
          {/* Header */}
          <div
            className="ag-chatbot-header"
            onClick={isMinimized ? toggleMinimize : undefined}
          >
            <div className="header-info">
              <div className="bot-avatar-wrap">
                <img src={botAvatarImg} alt="Bot Avatar" className="header-bot-img" />
                <span className="status-dot" title="Online" />
              </div>
              <div className="bot-details">
                <h4>Tara</h4>
                <span>Research & Publishing Assistant</span>
              </div>
            </div>

            <div className="header-actions">
              <button
                type="button"
                onClick={handleClearHistory}
                title="Restart Chat"
              >
                <Icons.Refresh />
              </button>
              <button
                type="button"
                onClick={toggleMinimize}
                title={isMinimized ? "Expand" : "Minimize"}
                aria-label={isMinimized ? "Expand" : "Minimize"}
              >
                {isMinimized ? <Icons.Expand /> : <Icons.Minimize />}
              </button>
              <button
                type="button"
                onClick={toggleOpen}
                title="Close"
              >
                <Icons.Close />
              </button>
            </div>
          </div>

          {/* Body & Input (hidden when minimized) */}
          {!isMinimized && (
            <>
              {/* Message List */}
              <div className="ag-chatbot-body">
                <div className="chat-date-divider">
                  <span>Today</span>
                </div>

                {messages.map((msg) => (
                  <div key={msg.id} className={`chat-message-row ${msg.sender}`}>
                    {msg.sender === "bot" && (
                      <div className="avatar-mini">
                        <img src={botAvatarImg} alt="Bot" className="msg-bot-img" />
                      </div>
                    )}
                    <div className="message-content">
                      <div className={`message-bubble ${msg.sender === "bot" ? "bot-bubble" : "user-bubble"}`}>
                        {msg.sender === "bot" ? (
                          <div className="chat-markdown-body" dangerouslySetInnerHTML={{ __html: formatChatMarkdown(msg.text) }} />
                        ) : (
                          msg.text
                        )}
                      </div>
                      <span className="message-time">{msg.time}</span>
                    </div>
                  </div>
                ))}

                {/* Typing / Loading Indicator */}
                {(isTyping || messages.length === 0) && (
                  <div className="chat-message-row bot">
                    <div className="avatar-mini">
                      <img src={botAvatarImg} alt="Bot" className="msg-bot-img" />
                    </div>
                    <div className="message-content">
                      <div className="message-bubble typing-indicator">
                        <span />
                        <span />
                        <span />
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Suggestion Chips (hidden after 2 user chats) */}
                {showSuggestions && (
                  <div className="chat-suggestions-wrap">
                    <div className="suggestions-label">Suggested Questions</div>
                    <div className="suggestions-list">
                      {INITIAL_SUGGESTIONS.map((suggestion, index) => (
                        <button
                          key={index}
                          className="suggestion-chip"
                          onClick={() => handleSendMessage(suggestion)}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Footer Input */}
              <div className="ag-chatbot-footer">
                <form
                  className="input-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendMessage();
                  }}
                >
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Type your question..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isTyping}
                  />
                  <button
                    type="submit"
                    className="send-btn"
                    disabled={!inputValue.trim() || isTyping}
                    title="Send message"
                  >
                    <Icons.Send />
                  </button>
                </form>
                <div className="footer-caption">
                  Powered by Aakashganga
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Floating Launcher Button */}
      <button
        className={`ag-chatbot-launcher ${isOpen ? "open" : ""}`}
        onClick={toggleOpen}
        title={isOpen ? "Close Chat" : "Chat with Tara"}
        aria-label="Toggle Tara chat assistant"
      >
        <span className="launcher-icon">
          {isOpen ? <Icons.Close /> : <img src={botAvatarImg} alt="Tara" className="launcher-bot-img" />}
        </span>
        {!isOpen && hasUnread && <span className="pulse-badge" />}
      </button>
    </div>
  );
};

export default ChatBot;
